import React from "react";
import { Entity } from "@common/types/entity";
import {
    ConsolidationGroupResponse,
    ConsolidationRelationshipResponse,
} from "@common/schemas/domains/accounting/consolidation.schema";
import { ConsolidationEntityRelationshipType } from "@common/types/domains/accounting/consolidation";
import { OwnershipSelection } from "../ownership.types";
import {
    isActiveRelationship,
    unorderedPairKey,
} from "../ownershipGraph.helpers";
import {
    UpdateGroupConfigCallbacks,
    UpdateGroupConfigParams,
} from "./ConsolidationBasisSelector";
import { ConsolidationGroupPane } from "./ConsolidationGroupPane";
import { EntityPane } from "./EntityPane";
import { RelationshipPane } from "./RelationshipPane";
import { RelationshipBundlePane } from "./RelationshipBundlePane";
import styles from "./OwnershipSidePanel.module.scss";

interface OwnershipSidePanelProps {
    workspaceId: string;
    selection: OwnershipSelection;
    entitiesByUuid: Map<string, Entity>;
    relationships: ConsolidationRelationshipResponse[];
    groups: ConsolidationGroupResponse[];
    onSelectionChange: (selection: OwnershipSelection) => void;
    onEditRelationship: (
        relationship: ConsolidationRelationshipResponse,
    ) => void;
    onDeleteRelationship: (relationshipId: string) => void;
    isUpdatingGroupConfig: boolean;
    onUpdateGroupConfig: (
        params: UpdateGroupConfigParams,
        callbacks?: UpdateGroupConfigCallbacks,
    ) => void;
}

// The entity pair whose bundle should show: from an explicit bundle selection,
// or the selected relationship's own pair (so the bundle stays mounted while a
// member is selected). Null when neither applies.
function bundlePairKey(
    selection: OwnershipSelection,
    selectedRelationship: ConsolidationRelationshipResponse | undefined,
): string | null {
    if (selection?.kind === "relationshipBundle") {
        return unorderedPairKey(
            selection.sourceEntityUuid,
            selection.targetEntityUuid,
        );
    }
    if (selectedRelationship) {
        return unorderedPairKey(
            selectedRelationship.sourceEntityUuid,
            selectedRelationship.targetEntityUuid,
        );
    }
    return null;
}

export const OwnershipSidePanel: React.FC<OwnershipSidePanelProps> = ({
    workspaceId,
    selection,
    entitiesByUuid,
    relationships,
    groups,
    onSelectionChange,
    onEditRelationship,
    onDeleteRelationship,
    isUpdatingGroupConfig,
    onUpdateGroupConfig,
}) => {
    if (!selection) {
        return null;
    }

    const selectedGroup =
        selection.kind === "group"
            ? groups.find(({ id }) => id === selection.groupId)
            : undefined;
    const selectedEntity =
        selection.kind === "entity"
            ? entitiesByUuid.get(selection.entityUuid)
            : undefined;
    const selectedRelationship =
        selection.kind === "relationship"
            ? relationships.find(({ id }) => id === selection.relationshipId)
            : undefined;
    const selectedPairKey = bundlePairKey(selection, selectedRelationship);
    const bundleRelationships =
        selectedPairKey !== null
            ? relationships.filter(
                  (relationship) =>
                      isActiveRelationship(relationship) &&
                      relationship.type !==
                          ConsolidationEntityRelationshipType.ownership &&
                      unorderedPairKey(
                          relationship.sourceEntityUuid,
                          relationship.targetEntityUuid,
                      ) === selectedPairKey,
              )
            : [];
    const isBundle = bundleRelationships.length > 1;

    if (
        !selectedGroup &&
        !selectedEntity &&
        !selectedRelationship &&
        !isBundle
    ) {
        return null;
    }

    return (
        <aside className={styles.panel}>
            <div className={styles.panelContent}>
                {selectedGroup && (
                    <ConsolidationGroupPane
                        workspaceId={workspaceId}
                        group={selectedGroup}
                        entitiesByUuid={entitiesByUuid}
                        isUpdatingGroupConfig={isUpdatingGroupConfig}
                        onUpdateGroupConfig={onUpdateGroupConfig}
                    />
                )}
                {selectedEntity && (
                    <EntityPane
                        entity={selectedEntity}
                        entitiesByUuid={entitiesByUuid}
                        relationships={relationships}
                        groups={groups}
                        onSelectionChange={onSelectionChange}
                    />
                )}
                {selectedRelationship && !isBundle && (
                    <RelationshipPane
                        relationship={selectedRelationship}
                        entitiesByUuid={entitiesByUuid}
                    />
                )}
                {isBundle && (
                    <RelationshipBundlePane
                        relationships={bundleRelationships}
                        entitiesByUuid={entitiesByUuid}
                        onSelectRelationship={(relationshipId) =>
                            onSelectionChange({
                                kind: "relationship",
                                relationshipId,
                            })
                        }
                        onEditRelationship={onEditRelationship}
                        onDeleteRelationship={onDeleteRelationship}
                    />
                )}
            </div>
        </aside>
    );
};
